// frontend/src/pages/TemplateList.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// 🌟 通知コンポーネントのインポート
import { Store } from 'react-notifications-component';
import 'react-notifications-component/dist/theme.css';

const TemplateList = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [activeDragId, setActiveDragId] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

  // 🌟 通知を表示する共通関数
  const showNotification = (type, title, message) => {
    Store.addNotification({
      title: title,
      message: message,
      type: type,
      insert: "top",
      container: "top-right",
      animationIn: ["animated", "fadeIn"],
      animationOut: ["animated", "fadeOut"],
      dismiss: { duration: 3000, onScreen: true },
      dismissable: { click: true }
    });
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${apiUrl}/barcode/templates`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('データの取得に失敗しました');
      const data = await response.json();
      setTemplates(data);
    } catch (error) {
      console.error('エラー:', error);
      showNotification('danger', '取得エラー', 'テンプレート一覧の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleDelete = async (uuid, name) => {
    if (!window.confirm(`テンプレート「${name}」を削除しますか？\nこの操作は取り消せません。`)) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${apiUrl}/barcode/templates/${uuid}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('削除に失敗しました');

      showNotification('info', '削除完了', 'テンプレートを削除しました。');
      fetchTemplates();
    } catch (error) {
      console.error('削除エラー:', error);
      showNotification('danger', 'エラー', '削除処理中にエラーが発生しました。');
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newTemplates = [...templates];
    const draggedItem = newTemplates[draggedIndex];

    newTemplates.splice(draggedIndex, 1);
    newTemplates.splice(index, 0, draggedItem);

    setTemplates(newTemplates);
    setDraggedIndex(index);
  };

  const handleDragEnd = async () => {
    setDraggedIndex(null);
    setActiveDragId(null);

    const sortedData = templates.map((temp, index) => ({
      uuid: temp.uuid,
      serial_num: index + 1
    }));

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${apiUrl}/barcode/templates/sort`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ data: sortedData })
      });

      if (!response.ok) throw new Error('並べ替えの保存に失敗しました');
      showNotification('info', '保存完了', '並べ替え順を保存しました。');
    } catch (error) {
      console.error('ソート保存エラー:', error);
      showNotification('danger', 'エラー', '並び順の保存に失敗しました。画面をリロードしてやり直してください。');
      fetchTemplates();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div>
      <h1 className="page-header">
        Barcode Templates <small>テンプレート管理</small>
      </h1>

      <div className="panel panel-inverse">
        <div className="panel-heading">
          <h4 className="panel-title">テンプレート一覧（ドラッグで並べ替え）</h4>
          <div className="panel-heading-btn">
            <button className="btn btn-xs btn-icon btn-circle btn-success" onClick={fetchTemplates} title="更新">
              <i className="fa fa-redo"></i>
            </button>
          </div>
        </div>

        <div className="panel-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">データを読み込んでいます...</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped table-bordered align-middle m-b-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '1%' }}></th>
                    <th style={{ width: '10%' }}>連番</th>
                    <th>テンプレート名</th>
                    <th style={{ width: '25%' }}>最終更新日時</th>
                    <th style={{ width: '20%' }}>アクション</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((temp, index) => (
                    <tr
                      key={temp.uuid}
                      draggable={activeDragId === temp.uuid}
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      style={{
                        backgroundColor: draggedIndex === index ? '#f8f9fa' : 'transparent',
                        opacity: draggedIndex === index ? 0.5 : 1,
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      <td
                        className="text-center text-muted"
                        style={{ cursor: 'ns-resize' }}
                        onMouseDown={() => setActiveDragId(temp.uuid)}
                        onMouseUp={() => setActiveDragId(null)}
                        onMouseLeave={() => setActiveDragId(null)}
                      >
                        <i className="fas fa-grip-vertical"></i>
                      </td>

                      <td className="text-center fw-bold text-secondary">
                        {temp.serial_num}
                      </td>

                      <td className="fw-bold text-dark">
                        {temp.name}
                      </td>

                      <td className="text-muted">
                        {formatDate(temp.modified_on)}
                      </td>

                      <td>
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                            onClick={() => navigate(`/barcode?uuid=${temp.uuid}`)}
                          >
                            <i className="fas fa-share fa-fw"></i> 編集・発行
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm d-flex align-items-center gap-1"
                            onClick={() => handleDelete(temp.uuid, temp.name)}
                          >
                            <i className="fas fa-trash-alt fa-fw"></i> 削除
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {templates.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-4">
                        保存されたテンプレートがありません。
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateList;